import { useEffect, useState } from 'react';
import { DT } from '../../theme/designTokens';
import { PageContent } from '../../components/ui/PageContent';
import { apiClient } from '../../network/ApiClient';
import type { AdminPresenceResponse, PresenceUser, PresenceActivity } from '../../types/game';

const POLL_INTERVAL_MS = 10_000;

// ── Activity config ───────────────────────────────────────────

const ACTIVITY_COLOR: Record<PresenceActivity, string> = {
  playing: '#4ADE80',
  waiting: DT.color.gold,
  online:  '#4A90D9',
  offline: DT.color.textMuted,
};

const ACTIVITY_LABEL: Record<PresenceActivity, string> = {
  playing: 'Playing',
  waiting: 'Waiting',
  online:  'Online',
  offline: 'Offline',
};

// ── Summary card ─────────────────────────────────────────────

function SummaryCard({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div
      style={{
        flex:         '1 1 140px',
        background:   DT.color.cardBg,
        border:       `1px solid ${DT.color.border}`,
        borderRadius: DT.radius.lg,
        padding:      '20px 24px',
      }}
    >
      <div
        style={{
          fontSize:      10,
          fontWeight:    700,
          letterSpacing: '1.2px',
          textTransform: 'uppercase',
          color:         DT.color.textMuted,
          fontFamily:    DT.font.sans,
          marginBottom:  10,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize:   34,
          fontWeight: 800,
          color,
          fontFamily: DT.font.heading,
          lineHeight: 1,
        }}
      >
        {value}
      </div>
    </div>
  );
}

// ── Online dot ────────────────────────────────────────────────

function OnlineDot({ online }: { online: boolean }) {
  return (
    <div
      style={{
        width:        8,
        height:       8,
        borderRadius: '50%',
        background:   online ? '#4ADE80' : DT.color.border,
        flexShrink:   0,
      }}
    />
  );
}

// ── Activity chip ─────────────────────────────────────────────

function ActivityChip({ activity }: { activity: PresenceActivity }) {
  const color = ACTIVITY_COLOR[activity];
  return (
    <span
      style={{
        fontSize:      10,
        fontWeight:    700,
        letterSpacing: '0.5px',
        color,
        background:    `${color}18`,
        border:        `1px solid ${color}44`,
        borderRadius:  4,
        padding:       '2px 7px',
        fontFamily:    DT.font.sans,
        whiteSpace:    'nowrap',
      }}
    >
      {ACTIVITY_LABEL[activity]}
    </span>
  );
}

// ── User row ──────────────────────────────────────────────────

function UserRow({ user, index }: { user: PresenceUser; index: number }) {
  const lastSeen = user.last_seen_at
    ? new Date(user.last_seen_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    : '—';

  return (
    <div
      style={{
        display:     'grid',
        gridTemplateColumns: '20px 1fr 80px 90px 80px 70px',
        gap:         12,
        alignItems:  'center',
        padding:     '10px 16px',
        borderBottom: `1px solid ${DT.color.border}`,
        background:  index % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)',
      }}
    >
      {/* Online dot */}
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <OnlineDot online={user.online} />
      </div>

      {/* Email */}
      <div
        style={{
          fontSize:     13,
          fontFamily:   DT.font.sans,
          color:        DT.color.textPrimary,
          overflow:     'hidden',
          textOverflow: 'ellipsis',
          whiteSpace:   'nowrap',
        }}
      >
        {user.email}
      </div>

      {/* Activity */}
      <div>
        <ActivityChip activity={user.activity} />
      </div>

      {/* Last seen */}
      <div
        style={{
          fontSize:  11,
          fontFamily: DT.font.sans,
          color:     DT.color.textMuted,
          whiteSpace: 'nowrap',
        }}
      >
        {lastSeen}
      </div>

      {/* Session ID */}
      <div
        style={{
          fontSize:  11,
          fontFamily: DT.font.sans,
          color:     user.session_id ? DT.color.textSecondary : DT.color.textMuted,
        }}
      >
        {user.session_id ? `#${user.session_id}` : '—'}
      </div>

      {/* Game ID + WS indicator */}
      <div
        style={{
          fontSize:  11,
          fontFamily: DT.font.sans,
          color:     user.game_id ? DT.color.textSecondary : DT.color.textMuted,
          display:   'flex',
          alignItems: 'center',
          gap:        4,
        }}
      >
        {user.game_id ? (
          <>
            #{user.game_id}
            {user.connected_to_game && (
              <span
                title="WebSocket connected"
                style={{
                  fontSize:   9,
                  fontWeight: 700,
                  color:      '#4ADE80',
                  background: 'rgba(74,222,128,0.12)',
                  border:     '1px solid rgba(74,222,128,0.3)',
                  borderRadius: 3,
                  padding:    '1px 4px',
                  letterSpacing: '0.4px',
                }}
              >
                WS
              </span>
            )}
          </>
        ) : '—'}
      </div>
    </div>
  );
}

// ── Table header ──────────────────────────────────────────────

function TableHeader() {
  const cell = (label: string) => (
    <div
      style={{
        fontSize:      10,
        fontWeight:    700,
        letterSpacing: '1px',
        textTransform: 'uppercase',
        color:         DT.color.textMuted,
        fontFamily:    DT.font.sans,
      }}
    >
      {label}
    </div>
  );
  return (
    <div
      style={{
        display:     'grid',
        gridTemplateColumns: '20px 1fr 80px 90px 80px 70px',
        gap:         12,
        alignItems:  'center',
        padding:     '10px 16px',
        borderBottom: `1px solid ${DT.color.borderGold}`,
        background:  DT.color.cardBg,
        borderRadius: `${DT.radius.lg} ${DT.radius.lg} 0 0`,
      }}
    >
      {cell('')}
      {cell('User')}
      {cell('Activity')}
      {cell('Last Seen')}
      {cell('Session')}
      {cell('Game')}
    </div>
  );
}

// ── Screen ────────────────────────────────────────────────────

export function AdminPresenceScreen() {
  const [data,    setData]    = useState<AdminPresenceResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  async function fetchData() {
    try {
      const res = await apiClient.getAdminPresence();
      setData(res);
      setLastRefresh(new Date());
      setError('');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      if (msg.includes('403') || msg.toLowerCase().includes('forbidden')) {
        setError('Access denied. You are not authorized to view this page.');
      } else {
        setError(`Failed to load presence data: ${msg}`);
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchData();
    const id = setInterval(fetchData, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  return (
    <div style={{ width: '100%', minHeight: '100%', background: DT.color.pageBg, paddingBottom: 80 }}>
      <PageContent>
        <div style={{ paddingTop: 32 }}>

          {/* Header */}
          <div style={{ marginBottom: 28 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <h1
                style={{
                  fontSize:   'clamp(24px, 3vw, 36px)',
                  fontWeight: 800,
                  fontFamily: DT.font.heading,
                  color:      DT.color.textPrimary,
                  margin:     0,
                  lineHeight: 1.1,
                }}
              >
                Presence
              </h1>
              <span
                style={{
                  fontSize:      9,
                  fontWeight:    700,
                  color:         '#dc2626',
                  background:    'rgba(220,38,38,0.12)',
                  border:        '1px solid rgba(220,38,38,0.28)',
                  borderRadius:  4,
                  padding:       '2px 6px',
                  letterSpacing: '0.8px',
                  textTransform: 'uppercase',
                  fontFamily:    DT.font.sans,
                }}
              >
                Internal
              </span>
            </div>
            <p style={{ margin: 0, fontSize: 13, color: DT.color.textMuted, fontFamily: DT.font.sans }}>
              Read-only user activity snapshot · Refreshes every 10 s
              {lastRefresh && (
                <span style={{ marginLeft: 12 }}>
                  · Last updated {lastRefresh.toLocaleTimeString()}
                </span>
              )}
            </p>
          </div>

          {/* Error state */}
          {error && (
            <div
              style={{
                padding:      '16px 20px',
                background:   'rgba(220,38,38,0.08)',
                border:       '1px solid rgba(220,38,38,0.3)',
                borderRadius: DT.radius.lg,
                color:        '#f87171',
                fontSize:     14,
                fontFamily:   DT.font.sans,
                marginBottom: 24,
              }}
            >
              {error}
            </div>
          )}

          {/* Summary cards */}
          {data && !error && (
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 32 }}>
              <SummaryCard label="Online Now"   value={data.total_online}  color="#4A90D9" />
              <SummaryCard label="Waiting"      value={data.total_waiting} color={DT.color.gold} />
              <SummaryCard label="Playing"      value={data.total_playing} color="#4ADE80" />
              <SummaryCard
                label="Total Tracked"
                value={data.users.length}
                color={DT.color.textSecondary}
              />
            </div>
          )}

          {/* User table */}
          {loading && !data ? (
            <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 48 }}>
              <div
                style={{
                  width:          24,
                  height:         24,
                  borderRadius:   '50%',
                  border:         `2px solid ${DT.color.border}`,
                  borderTopColor: DT.color.gold,
                  animation:      'spin 0.75s linear infinite',
                }}
              />
            </div>
          ) : data && !error ? (
            data.users.length === 0 ? (
              <div
                style={{
                  padding:   '48px 20px',
                  textAlign: 'center',
                  color:     DT.color.textMuted,
                  fontFamily: DT.font.sans,
                  fontSize:  14,
                }}
              >
                No users tracked yet. Presence data accumulates as users make API calls.
              </div>
            ) : (
              <div
                style={{
                  border:       `1px solid ${DT.color.border}`,
                  borderRadius: DT.radius.lg,
                  overflow:     'hidden',
                }}
              >
                <TableHeader />
                {data.users.map((user, i) => (
                  <UserRow key={user.user_id} user={user} index={i} />
                ))}
              </div>
            )
          ) : null}

          {/* Legend */}
          {data && !error && (
            <div
              style={{
                marginTop:  24,
                padding:    '12px 16px',
                background: DT.color.cardBg,
                border:     `1px solid ${DT.color.border}`,
                borderRadius: DT.radius.lg,
                display:    'flex',
                flexWrap:   'wrap',
                gap:        20,
              }}
            >
              <div
                style={{
                  fontSize:      10,
                  fontWeight:    700,
                  letterSpacing: '1px',
                  textTransform: 'uppercase',
                  color:         DT.color.textMuted,
                  fontFamily:    DT.font.sans,
                  alignSelf:     'center',
                  flexShrink:    0,
                }}
              >
                Data sources:
              </div>
              {[
                { label: 'Online / Last Seen', source: 'Redis ZSET presence:active (HTTP activity)' },
                { label: 'Session / Activity', source: 'PostgreSQL session_users + sessions' },
                { label: 'Game ID',            source: 'PostgreSQL games (inferred from active session)' },
                { label: 'WS badge',           source: 'Redis SET game_{id}_users (GameChannel)' },
              ].map(({ label, source }) => (
                <div key={label} style={{ fontFamily: DT.font.sans }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: DT.color.textSecondary }}>{label}: </span>
                  <span style={{ fontSize: 11, color: DT.color.textMuted }}>{source}</span>
                </div>
              ))}
            </div>
          )}

        </div>
      </PageContent>
    </div>
  );
}
